import React, { useEffect, useReducer, useState } from "react";
import clone from "lodash.clonedeep";
import { Table } from "react-bootstrap";
import { participantsTableHead } from "../models";
import ParticipantsTableRow from "./ParticipantsTableRow";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import * as constants from "../constants";
import FilterParticipants from "./FilterParticipants";

const ParticipantsTable = (
    {
        participants,
        tags,
        roles,
        ids,
        onEdit,
        onDelete,
        onChangeOrder,
        onHideMember,
        onToggleMember,
        onToggleAllMembers,
    }
) => {
    const [visibleParticipants, setVisibleParticipants] = useState([]);
    const [filters, setFilters] = useReducer((prev, next) => {
            return { ...prev, ...next };
        },
        {
            [constants.FIELD_ID]: null,
            [constants.FIELD_LASTNAME]: null,
            [constants.FIELD_NAME]: null,
            [constants.FIELD_PATRONYMIC]: null,
            [constants.FIELD_COMPANY]: null,
            [constants.FIELD_ROLES]: null,
            [constants.FIELD_TAGS]: null,
        },
    );

    useEffect(() => {
        setVisibleParticipants(participants || []);
    }, [participants]);

    useEffect(() => {
        handleFilterParticipants();
    }, [filters]);

    const handleChangeFilter = (key, value) => {
        setFilters({[key]: value});
    };

    function handleFilterParticipants() {
        let filteredList = participants;

        if (filters?.[constants.FIELD_ID]) {
            filteredList = filteredList.filter(({id}) => id.toString().includes(filters[constants.FIELD_ID]));
        }

        if (filters[constants.FIELD_LASTNAME]) {
            filteredList = filteredList.filter(({lastnameRu, lastnameEn}) => {
                return lastnameRu.toLowerCase().includes(filters[constants.FIELD_LASTNAME])
                    || lastnameEn.toLowerCase().includes(filters[constants.FIELD_LASTNAME]);
            });
        }

        if (filters[constants.FIELD_NAME]) {
            filteredList = filteredList.filter(({nameRu, nameEn}) => {
                return nameRu.toLowerCase().includes(filters[constants.FIELD_NAME])
                    || nameEn.toLowerCase().includes(filters[constants.FIELD_NAME]);
            });
        }

        if (filters[constants.FIELD_PATRONYMIC]) {
            filteredList = filteredList.filter(({patronymicRu}) => {
                return patronymicRu.toLowerCase().includes(filters[constants.FIELD_PATRONYMIC]);
            });
        }

        if (filters[constants.FIELD_COMPANY]) {
            filteredList = filteredList.filter(({companyRu, companyEn}) => {
                return companyRu.toLowerCase().includes(filters[constants.FIELD_COMPANY]) 
                    | companyEn.toLowerCase().includes(filters[constants.FIELD_COMPANY]);
            });
        }

        if (filters[constants.FIELD_ROLES]) {
            filteredList = filteredList.filter(({roles}) => {
                return roles.some(({titleRu, titleEn}) => {
                    return titleRu.toLowerCase().includes(filters[constants.FIELD_ROLES])
                        || titleEn.toLowerCase().includes(filters[constants.FIELD_ROLES]);
                })
            });
        }

        if (filters[constants.FIELD_TAGS]) {
            filteredList = filteredList.filter(({tags}) => {
                return tags.some(({titleRu, titleEn}) => {
                    return titleRu.toLowerCase().includes(filters[constants.FIELD_TAGS])
                        || titleEn.toLowerCase().includes(filters[constants.FIELD_TAGS]);
                })
            });
        }

        setVisibleParticipants(filteredList);
    };

    function onDragEnd(result) {
        if (!result.destination) {
            return;
        }

        const reorderedParticipants = reorder(
            participants,
            result.source.index,
            result.destination.index,
        );

        onChangeOrder(reorderedParticipants);
    }

    function reorder(list, startIndex, endIndex) {
        const result = clone(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    }

    return (
        <Table>
            <thead>
                <tr>
                    <th style={{width: '56px'}} />
                    {Object.entries(participantsTableHead).map(([key, value]) => (
                        <th key={key}>
                            {value}
                        </th>
                    ))}
                    <th style={{width: '56px'}} />
                    <th style={{width: '56px'}} />
                </tr>
                <tr>
                    <th style={{width: '56px'}}>
                        <input
                            type="checkbox"
                            checked={Object.keys(ids).length === participants?.length}
                            onChange={onToggleAllMembers}
                        />
                    </th>
                    <FilterParticipants
                        filters={filters}
                        onChange={handleChangeFilter}
                    />
                    <th style={{width: '56px'}} />
                    <th style={{width: '56px'}} />
                </tr>
            </thead>
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable">
                    {(provided) => (
                        <tbody
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                        >
                            {visibleParticipants?.map((participant, index) => (
                                <Draggable
                                    key={participant.id}
                                    draggableId={`${participant.id || index}`}
                                    index={index}
                                >
                                    {(provided) => (
                                        <tr
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            style={
                                                provided.draggableProps.style
                                            }
                                        >
                                            <ParticipantsTableRow
                                                key={participant.id}
                                                participant={participant}
                                                tags={tags}
                                                roles={roles}
                                                checked={ids.includes(participant.id)}
                                                onEdit={onEdit}
                                                onDelete={onDelete}
                                                onHideMember={onHideMember}
                                                onToggleMember={onToggleMember}
                                            />
                                        </tr>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </tbody>
                    )}
                </Droppable>
            </DragDropContext>
        </Table>
    );
};

export default ParticipantsTable;
